const tk = (value) => value;
/**
 * Canonical semantic concepts for fields that do not own a more-specific help key.
 *
 * Keep this mapping explicit. Production quality checks distinguish these semantic
 * mappings from ID/type fallbacks so a newly-added field cannot silently receive
 * vague copy such as "numeric value" or "selection" and still pass conformance.
 */
const NAME_TO_HELP_KEY = Object.freeze({
    email: 'helpText.semantic.email',
    firstName: 'helpText.semantic.firstName',
    lastName: 'helpText.semantic.lastName',
    fullName: 'helpText.semantic.fullName',
    password: 'helpText.semantic.password',
    currentPassword: 'helpText.semantic.currentPassword',
    newPassword: 'helpText.semantic.newPassword',
    confirmPassword: 'helpText.semantic.confirmPassword',
    adminEmail: 'helpText.semantic.adminEmail',
    adminPassword: 'helpText.semantic.adminPassword',
    roles: 'helpText.semantic.roles',
    roleId: 'helpText.semantic.role',
    roleCode: 'helpText.semantic.roleCode',
    permissions: 'helpText.semantic.permissions',
    status: 'helpText.semantic.status',
    code: 'helpText.semantic.code',
    name: 'helpText.semantic.name',
    description: 'helpText.semantic.description',
    notes: 'helpText.semantic.notes',
    reason: 'helpText.semantic.reason',
    cancellationReason: 'helpText.semantic.cancellationReason',
    branchId: 'helpText.semantic.branch',
    targetBranchId: 'helpText.semantic.branch',
    parentBranchId: 'helpText.semantic.parentBranch',
    defaultBranchCode: 'helpText.semantic.defaultBranchCode',
    defaultBranchName: 'helpText.semantic.defaultBranchName',
    organizationId: 'helpText.semantic.organization',
    targetOrganizationId: 'helpText.semantic.targetOrganization',
    organizationName: 'helpText.semantic.organizationName',
    clientId: 'helpText.semantic.client',
    clientAccountId: 'helpText.semantic.clientAccount',
    targetClientAccountId: 'helpText.semantic.clientAccount',
    portfolioId: 'helpText.semantic.portfolio',
    portfolioAccountId: 'helpText.semantic.custodyAccount',
    targetPortfolioAccountId: 'helpText.semantic.portfolio',
    custodyAccountId: 'helpText.semantic.custodyAccount',
    accountId: 'helpText.semantic.account',
    accountNumber: 'helpText.semantic.accountNumber',
    accountType: 'helpText.semantic.accountType',
    baseCurrency: 'helpText.semantic.baseCurrency',
    currency: 'helpText.semantic.currency',
    symbol: 'helpText.semantic.instrumentSymbol',
    instrumentSymbol: 'helpText.semantic.instrumentSymbol',
    replacementInstrumentSymbol: 'helpText.semantic.replacementInstrumentSymbol',
    instrumentId: 'helpText.semantic.instrument',
    instrumentName: 'helpText.semantic.instrumentName',
    securityName: 'helpText.semantic.securityName',
    instrumentType: 'helpText.semantic.instrumentType',
    sector: 'helpText.semantic.sector',
    isin: 'helpText.semantic.isin',
    exchange: 'helpText.semantic.exchange',
    txnType: 'helpText.semantic.transactionType',
    transactionType: 'helpText.semantic.transactionType',
    transactionId: 'helpText.semantic.transactionId',
    transactionDate: 'helpText.semantic.transactionDate',
    settlementDate: 'helpText.semantic.settlementDate',
    quantity: 'helpText.semantic.quantity',
    expectedQuantity: 'helpText.semantic.expectedQuantity',
    price: 'helpText.semantic.price',
    pricePerUnit: 'helpText.semantic.pricePerUnit',
    marketPrice: 'helpText.semantic.marketPrice',
    observationKind: 'helpText.semantic.marketPrice',
    open: 'helpText.semantic.marketPrice',
    high: 'helpText.semantic.marketPrice',
    low: 'helpText.semantic.marketPrice',
    close: 'helpText.semantic.marketPrice',
    last: 'helpText.semantic.marketPrice',
    volume: 'helpText.semantic.marketPrice',
    turnover: 'helpText.semantic.marketPrice',
    tradeCount: 'helpText.semantic.marketPrice',
    priceDate: 'helpText.semantic.priceDate',
    tradedDate: 'helpText.semantic.tradedDate',
    valuationDate: 'helpText.semantic.valuationDate',
    commission: 'helpText.semantic.commission',
    sebonFee: 'helpText.semantic.sebonFee',
    dpCharge: 'helpText.semantic.dpCharge',
    dpCharges: 'helpText.semantic.dpCharge',
    fees: 'helpText.semantic.fees',
    charges: 'helpText.semantic.charges',
    externalReference: 'helpText.semantic.externalReference',
    sourceReference: 'helpText.semantic.sourceReference',
    source: 'helpText.semantic.source',
    freshness: 'helpText.semantic.freshness',
    importType: 'helpText.semantic.importType',
    file: 'helpText.semantic.file',
    sourceFileName: 'helpText.semantic.file',
    sourceSheetName: 'helpText.semantic.sourceSheetName',
    phone: 'helpText.semantic.phone',
    panNumber: 'helpText.semantic.panNumber',
    legalName: 'helpText.semantic.legalName',
    sebonRegistrationNumber: 'helpText.semantic.sebonRegistrationNumber',
    jurisdiction: 'helpText.semantic.jurisdiction',
    locale: 'helpText.semantic.locale',
    timeZone: 'helpText.semantic.timeZone',
    calendarSystem: 'helpText.semantic.calendarSystem',
    isDefault: 'helpText.semantic.isDefault',
    isActive: 'helpText.semantic.isActive',
    strategy: 'helpText.semantic.strategy',
    mandateStartDate: 'helpText.semantic.mandateStartDate',
    mandateEndDate: 'helpText.semantic.mandateEndDate',
    effectiveFrom: 'helpText.semantic.effectiveFrom',
    effectiveTo: 'helpText.semantic.effectiveTo',
    effectiveDate: 'helpText.semantic.effectiveDate',
    graceEndsAt: 'helpText.semantic.graceEndsAt',
    createdAt: 'helpText.semantic.createdAt',
    updatedAt: 'helpText.semantic.updatedAt',
    approvedAt: 'helpText.semantic.approvedAt',
    expiresAt: 'helpText.semantic.expiresAt',
    taxRate: 'helpText.semantic.taxRate',
    rate: 'helpText.semantic.rate',
    taxType: 'helpText.semantic.taxType',
    holdingPeriodDays: 'helpText.semantic.holdingPeriodDays',
    holdingPeriod: 'helpText.semantic.holdingPeriodDays',
    holdingPeriodMinimumDays: 'helpText.semantic.holdingPeriodMinimumDays',
    holdingPeriodClassification: 'helpText.semantic.holdingPeriodClassification',
    regulatoryProfileReady: 'helpText.semantic.regulatoryProfileReady',
    assignedModules: 'helpText.semantic.assignedModules',
    branchCount: 'helpText.semantic.branchCount',
    moduleId: 'helpText.semantic.module',
    moduleKey: 'helpText.semantic.module',
    module: 'helpText.semantic.module',
    modules: 'helpText.semantic.modules',
    maxBranches: 'helpText.semantic.maxBranches',
    maxUsers: 'helpText.semantic.maxUsers',
    configurationVersionId: 'helpText.semantic.configurationVersionId',
    startDate: 'helpText.semantic.startDate',
    endDate: 'helpText.semantic.endDate',
    fromDate: 'helpText.semantic.startDate',
    toDate: 'helpText.semantic.endDate',
    dateFrom: 'helpText.semantic.startDate',
    dateTo: 'helpText.semantic.endDate',
    from: 'helpText.semantic.transactionPeriodStart',
    to: 'helpText.semantic.transactionPeriodEnd',
    periodStart: 'helpText.semantic.periodStart',
    periodEnd: 'helpText.semantic.periodEnd',
    reportingPeriodStart: 'helpText.semantic.reportingPeriodStart',
    reportingPeriodEnd: 'helpText.semantic.reportingPeriodEnd',
    asOfDate: 'helpText.semantic.asOfDate',
    cutoffAt: 'helpText.semantic.cutoffAt',
    fiscalYear: 'helpText.semantic.fiscalYear',
    year: 'helpText.semantic.year',
    month: 'helpText.semantic.month',
    quarter: 'helpText.semantic.quarter',
    outputFormat: 'helpText.semantic.outputFormat',
    format: 'helpText.semantic.outputFormat',
    grouping: 'helpText.semantic.grouping',
    comparisonPeriod: 'helpText.semantic.comparisonPeriod',
    reportType: 'helpText.semantic.reportType',
    reportCode: 'helpText.semantic.reportCode',
    reportName: 'helpText.semantic.reportName',
    regulatoryBody: 'helpText.semantic.regulatoryBody',
    reportDefinitionId: 'helpText.semantic.reportDefinition',
    runId: 'helpText.semantic.reconciliationRun',
    severity: 'helpText.semantic.severity',
    type: 'helpText.semantic.transactionActivityType',
    instructionType: 'helpText.semantic.instructionType',
    settlementMethod: 'helpText.semantic.settlementMethod',
    bankAccount: 'helpText.semantic.bankAccount',
    bankAccountNumber: 'helpText.semantic.bankAccount',
    dematAccount: 'helpText.semantic.dematAccount',
    dematAccountNumber: 'helpText.semantic.dematAccount',
    expectedCash: 'helpText.semantic.expectedCash',
    expectedDate: 'helpText.semantic.expectedSettlementDate',
    externalMessageId: 'helpText.semantic.externalMessageId',
    sequence: 'helpText.semantic.settlementSequence',
    cash: 'helpText.semantic.settledCash',
    actionType: 'helpText.semantic.corporateActionType',
    recordDate: 'helpText.semantic.recordDate',
    exDate: 'helpText.semantic.exDate',
    declarationDate: 'helpText.semantic.declarationDate',
    bookCloseDate: 'helpText.semantic.bookCloseDate',
    payDate: 'helpText.semantic.payDate',
    ratioNumerator: 'helpText.semantic.ratioNumerator',
    ratioDenominator: 'helpText.semantic.ratioDenominator',
    cashRate: 'helpText.semantic.cashRate',
    rightPrice: 'helpText.semantic.rightPrice',
    authority: 'helpText.semantic.licenseAuthority',
    licenseType: 'helpText.semantic.licenseType',
    licenseNumber: 'helpText.semantic.licenseNumber',
    licenses: 'helpText.semantic.licenses',
    authorityTemplateVersion: 'helpText.semantic.authorityTemplateVersion',
    authorityEffectiveFrom: 'helpText.semantic.authorityEffectiveFrom',
    authorityEffectiveTo: 'helpText.semantic.authorityEffectiveTo',
    theme: 'helpText.semantic.theme',
    language: 'helpText.semantic.language',
    defaultContext: 'helpText.semantic.defaultContext',
    dateFormat: 'helpText.semantic.dateFormat',
    numberFormat: 'helpText.semantic.numberFormat',
    defaultPageSize: 'helpText.semantic.defaultPageSize',
    supportUserIdentityId: 'helpText.semantic.delegation',
    purpose: 'helpText.semantic.delegation',
    ticketReference: 'helpText.semantic.delegation',
    justification: 'helpText.semantic.delegation',
    capabilities: 'helpText.semantic.delegation',
    allowedOperationIds: 'helpText.semantic.delegation',
    resourceIds: 'helpText.semantic.delegation',
    requiredAal: 'helpText.semantic.delegation',
    policyVersion: 'helpText.semantic.delegation',
    sessionStartedAt: 'helpText.semantic.delegation',
    sessionEndedAt: 'helpText.semantic.delegation',
});
function helpKeyFromId(id) {
    const value = id.toLowerCase();
    if (value.includes('permission'))
        return 'helpText.semantic.permissions';
    if (value.includes('branch'))
        return 'helpText.semantic.branch';
    if (value.includes('portfolio'))
        return 'helpText.semantic.portfolio';
    if (value.includes('client') && value.includes('account'))
        return 'helpText.semantic.clientAccount';
    if (value.includes('client'))
        return 'helpText.semantic.client';
    if (value.includes('instrument') || value.includes('security'))
        return 'helpText.semantic.instrument';
    if (value.includes('transaction'))
        return 'helpText.semantic.transaction';
    if (value.includes('tax'))
        return 'helpText.semantic.tax';
    if (value.includes('market') && value.includes('price'))
        return 'helpText.semantic.marketPrice';
    if (value.includes('settlement'))
        return 'helpText.semantic.settlement';
    if (value.includes('module'))
        return 'helpText.semantic.module';
    if (value.includes('report'))
        return 'helpText.semantic.reportParameter';
    if (value.includes('delegation'))
        return 'helpText.semantic.delegation';
    if (value.includes('organization'))
        return 'helpText.semantic.organization';
    if (value.includes('opening'))
        return 'helpText.semantic.openingBalance';
    return undefined;
}
function fallbackKey(fieldType) {
    switch (fieldType) {
        case 'date':
        case 'datetime':
            return 'helpText.semantic.date';
        case 'number':
        case 'decimal':
        case 'percentage':
        case 'quantity':
        case 'money':
            return 'helpText.semantic.numericValue';
        case 'select':
        case 'multiselect':
            return 'helpText.semantic.selection';
        case 'checkbox':
        case 'switch':
            return 'helpText.semantic.setting';
        case 'file':
            return 'helpText.semantic.file';
        case 'collection':
            return 'helpText.semantic.collection';
        case 'textarea':
            return 'helpText.semantic.notes';
        default:
            return 'helpText.semantic.textValue';
    }
}
/**
 * Resolve a help key and report how it was resolved. Production conformance
 * requires auto-annotated visible fields to resolve by semantic field name.
 * ID/type fallbacks remain only as a runtime safety net and are not considered
 * production-quality annotation copy.
 */
export function resolveCanonicalFieldHelpTextKey(id, name, fieldType) {
    const nameKey = NAME_TO_HELP_KEY[name];
    if (nameKey)
        return { key: tk(nameKey), source: 'name' };
    const idKey = helpKeyFromId(id);
    if (idKey)
        return { key: tk(idKey), source: 'id' };
    return { key: tk(fallbackKey(fieldType)), source: 'type' };
}
export function getCanonicalFieldHelpTextKey(id, name, fieldType) {
    return resolveCanonicalFieldHelpTextKey(id, name, fieldType).key;
}
function annotateCollectionColumn(parent, column) {
    if (column.helpTextKey)
        return column;
    return {
        ...column,
        helpTextKey: getCanonicalFieldHelpTextKey(`${String(parent.id)}.${column.name}`, column.name, column.fieldType),
    };
}
export function withCanonicalFieldAnnotations(form) {
    return {
        ...form,
        fields: form.fields.map((field) => {
            if (field.fieldType === 'hidden')
                return field;
            return {
                ...field,
                helpTextKey: field.helpTextKey ?? getCanonicalFieldHelpTextKey(String(field.id), field.name, field.fieldType),
                collection: field.collection
                    ? {
                        ...field.collection,
                        columns: field.collection.columns.map((column) => annotateCollectionColumn(field, column)),
                    }
                    : field.collection,
            };
        }),
    };
}
export function withCanonicalFieldAnnotationsRegistry(registry) {
    return Object.freeze(Object.fromEntries(Object.entries(registry).map(([id, form]) => [id, withCanonicalFieldAnnotations(form)])));
}
